import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { payslipKeys, employeeKeys } from '@/lib/query-keys';
import { payslipService, payslipEmailService } from '@/services/payroll.service';
import { usePayslipFilterStore } from '@/stores/payslipFilterStore';

function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
}

export function usePayslips() {
  const queryClient = useQueryClient();
  const filters = usePayslipFilterStore();

  // The store keeps month/year as strings so the Select components can hold
  // them; the API wants numbers, and empty strings must not be sent at all.
  const toApiParams = (f: typeof filters.current) => {
    const params: Record<string, unknown> = { ...f };
    if (params.periodMonth) params.periodMonth = Number(params.periodMonth);
    else delete params.periodMonth;
    if (params.periodYear) params.periodYear = Number(params.periodYear);
    else delete params.periodYear;
    return params;
  };

  const payslipsQuery = useQuery({
    queryKey: payslipKeys.list(filters.current),
    queryFn: () => payslipService.getAll(toApiParams(filters.current)),
    placeholderData: keepPreviousData,
  });

  const invalidate = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: payslipKeys.lists() });
    if (id) queryClient.invalidateQueries({ queryKey: payslipKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: employeeKeys.all });
  };

  const generate = useMutation({
    mutationFn: (data: Record<string, unknown>) => payslipService.generate(data),
    onSuccess: () => { invalidate(); toast.success('Payslip generated'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to generate payslip')),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => payslipService.update(id, data),
    onSuccess: (_, { id }) => { invalidate(id); toast.success('Payslip updated'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to update payslip')),
  });

  const publish = useMutation({
    mutationFn: (id: string) => payslipService.publish(id),
    onSuccess: (_, id) => { invalidate(id); toast.success('Payslip published'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to publish payslip')),
  });

  const markPaid = useMutation({
    mutationFn: (id: string) => payslipService.markPaid(id),
    onSuccess: (_, id) => { invalidate(id); toast.success('Marked as paid'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to mark as paid')),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => payslipService.cancel(id),
    onSuccess: (_, id) => { invalidate(id); toast.success('Payslip cancelled'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to cancel payslip')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => payslipService.delete(id),
    onSuccess: () => { invalidate(); toast.success('Payslip deleted'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to delete payslip')),
  });

  const supersede = useMutation({
    mutationFn: ({ id, data }: { id: string; data?: Record<string, unknown> }) =>
      payslipService.supersede(id, data),
    onSuccess: (res, { id }) => {
      // Both ends change: the original becomes SUPERSEDED and a new draft exists.
      invalidate(id);
      queryClient.invalidateQueries({ queryKey: payslipKeys.detail(res.data.id) });
      toast.success(`Correction ${res.data.payslipNumber} created as a draft`);
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to correct the payslip')),
  });

  const updateDetails = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      payslipService.updateDetails(id, data),
    onSuccess: (_, { id }) => { invalidate(id); toast.success('Details corrected'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to correct the details')),
  });

  const emailPayslip = useMutation({
    mutationFn: (id: string) => payslipEmailService.emailOne(id),
    onSuccess: (res, id) => {
      invalidate(id);
      const to = (res.data.queued?.[0] as { to?: string } | undefined)?.to;
      toast.success(to ? `Payslip queued for ${to}` : 'Payslip queued for sending');
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to queue the payslip email')),
  });

  return {
    emailPayslip,
    payslips: payslipsQuery.data?.data ?? [],
    meta: payslipsQuery.data?.meta,
    isLoading: payslipsQuery.isLoading,
    filters,
    generate, update, publish, markPaid, cancel, remove, supersede, updateDetails,
    exportCsv: () => payslipService.exportCsv(toApiParams(filters.current)),
  };
}

export function usePayslip(id: string) {
  const query = useQuery({
    queryKey: payslipKeys.detail(id),
    queryFn: () => payslipService.getById(id),
    enabled: !!id,
  });
  return { payslip: query.data?.data, isLoading: query.isLoading };
}

/**
 * Live payslip preview. A mutation rather than a query so the caller decides
 * exactly when it recomputes (debounced as the period or attendance changes).
 */
export function usePayslipPreview() {
  const preview = useMutation({
    mutationFn: (data: Parameters<typeof payslipService.preview>[0]) => payslipService.preview(data),
  });
  return {
    preview,
    data: preview.data?.data,
    error: preview.isError ? apiMessage(preview.error, 'Could not compute the payslip') : null,
    isComputing: preview.isPending,
  };
}
